/*
Copyright (C) 2020 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React from 'react';
import cx from 'classnames';
import { capitalize, find, lowerCase, startCase } from 'lodash';

import { DateTime } from '@broadleaf/admin-components/dist/common/components/DateTime';
import PlaceholderBlock from '@broadleaf/admin-components/dist/common/components/PlaceholderBlock';
import {
  getCustomerEmail,
  getCustomerName,
  getFulfillmentOption,
  getFulfillmentAddress,
  getParentOrderNumber,
  getSubmitDate
} from '@broadleaf/admin-components/dist/oms/utils/FulfillmentUtils';
import { getFulfillmentStatuses } from '@broadleaf/admin-components/dist/oms/utils/OrderStatusUtils';
import { getAttribute } from '@broadleaf/admin-components/dist/metadata/utils/MetadataUtils';
import { useFormatDate } from '@broadleaf/admin-components/dist/common';
import { IFulfillmentViewCommon } from '@broadleaf/admin-components/dist/oms/components/FulfillmentView/fulfillment';
import Currency from '@broadleaf/admin-components/dist/common/components/Currency';
import {
  getBuyerFacility,
  getInventoryLocationName,
  getSellerFacilities,
  getShippingOptionMessages,
  IOrderFulfillmentCommissions
} from '@src/oms/utils/RmFulfillmentUtils';
import { useUserProfile } from '@broadleaf/admin-components/dist/authentication';
import { useFormatNumber } from '@broadleaf/admin-components/dist/common';
import { getAllShipments } from '@src/oms/FulfillmentView/util/ShipmentUtils';
import useSecureAssetUrl from '@src/hooks/useSecureAssetUrl';

export const Details: React.FC<DetailsProps> = ({
  className,
  metadata,
  state
}) => {
  const formatDate = useFormatDate();
  const fulfillment = state.data;

  const order = fulfillment?.order;
  const { fullAssetUrl: taxExemptDocumentUrl } = useSecureAssetUrl(
    order?.attributes['taxExemptDocument']?.contentUrl
  );

  if (!fulfillment) {
    return (
      <div className={cx(className, 'tw-mx-auto tw-w-full tw-py-4 md:tw-px-4')}>
        <PlaceholderBlock height="300" width="full" />
      </div>
    );
  }
  const isTaxExempt =
    (order?.attributes &&
      'isTaxExempt' in order.attributes &&
      order.attributes['isTaxExempt'] === true) ||
    (order?.internalAttributes &&
      'taxId' in order.internalAttributes &&
      order.internalAttributes['taxId'] !== null);
  const orderId = fulfillment.orderId;
  const orderNumber = getParentOrderNumber(fulfillment);
  const submitDate = getSubmitDate(fulfillment);
  const fulfillmentType = fulfillment.type;
  const fulfillmentOption = getFulfillmentOption(fulfillment);
  const fulfillmentAddress = getFulfillmentAddress(fulfillment);
  const customerName = getCustomerName(fulfillment);
  const customerEmail = getCustomerEmail(fulfillment);
  const shipments = getAllShipments(fulfillment).filter(
    s => s.shipmentStatus !== 'VOIDED'
  );
  const sellerFacilities = getSellerFacilities(fulfillment);
  const buyerFacility = getBuyerFacility(fulfillment);
  const inventoryLocationName = getInventoryLocationName(fulfillment);

  const invoiceCreditProvider =
    fulfillment.attributes['paymentType'] === 'INVOICE_CREDIT' ||
    fulfillment.attributes['paymentType'] === 'STORE_CREDIT'
      ? capitalize(fulfillment.attributes['invoiceCreditProvider'] || 'REMITRA')
      : null;
  const paymentType =
    fulfillment.attributes['paymentType'] === 'INVOICE_CREDIT' ||
    fulfillment.attributes['paymentType'] === 'STORE_CREDIT'
      ? 'Invoice'
      : fulfillment.attributes['paymentType'] === 'PAYPAL'
      ? 'PayPal'
      : fulfillment.attributes['paymentType'] === 'PAYPAL_CHECKOUT'
      ? 'PayPal Checkout'
      : fulfillment.attributes['paymentType'] === 'REVMED_CREDIT'
      ? 'RevMed Credit'
      : startCase(lowerCase(fulfillment.attributes['paymentType'] || 'Other'));
  const estimatedDeliveryDate = fulfillment.attributes['estimatedDeliveryDate'];
  const shippingOptionMessages = getShippingOptionMessages(fulfillment);

  return (
    <div className={cx(className, 'tw-flex tw-flex-col tw-p-4')}>
      <div className="tw-flex tw-flex-row">
        <FulfillmentStatusIndicator metadata={metadata} state={state} />
      </div>
      <div className="tw-flex tw-flex-col md:tw-flex-row print:tw-flex-row">
        <div className="tw-flex tw-flex-1 tw-flex-col">
          <LabeledText className="tw-flex-1" label="Seller Facility Name(s)">
            <ul>
              {sellerFacilities?.map((sellerFacility, index) => (
                <li key={index}>
                  {sellerFacility.accountName}{' '}
                  {sellerFacility.salesRep && (
                    <span className=" tw-text-xs tw-font-semibold">
                      ({sellerFacility.salesRep.name})
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </LabeledText>
          {(sellerFacilities.length !== 1 ||
            inventoryLocationName !== sellerFacilities[0].accountName) && (
            <LabeledText className="tw-flex-1" label="Inventory Location Name">
              {inventoryLocationName}
            </LabeledText>
          )}
          <LabeledText className="tw-flex-1" label="Buyer Facility Name">
            <ul>
              <li>
                {buyerFacility?.accountName}{' '}
                {buyerFacility?.salesRep && (
                  <span className=" tw-text-xs tw-font-semibold">
                    ({buyerFacility.salesRep.name})
                  </span>
                )}
              </li>
            </ul>
          </LabeledText>
          {fulfillmentAddress && (
            <div className="tw-flex tw-flex-col md:tw-flex-row">
              {fulfillmentAddress && (
                <LabeledText className="tw-flex-1" label="Ship To Address">
                  <FulfillmentAddress address={fulfillmentAddress} />
                </LabeledText>
              )}
            </div>
          )}
        </div>

        <div className="tw-flex-1">
          <LabeledText className="tw-flex-1" label="Parent Order Number">
            <a
              className="tw-text-blue-700"
              href={`/orders/${orderId}`}
              target="_blank"
              rel="noreferrer"
            >
              <span title={orderNumber}>{orderNumber}</span>
            </a>
          </LabeledText>
          <LabeledText className="tw-flex-1" label="Date Submitted">
            <DateTime value={submitDate} />
          </LabeledText>

          <LabeledText className="tw-flex-1" label="Tax Exempt">
            <div>
              {isTaxExempt ? 'Yes' : 'No'}{' '}
              {order?.attributes['taxExemptDocument'] &&
              taxExemptDocumentUrl ? (
                <a
                  href={taxExemptDocumentUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="tw-inline-block tw-text-xs tw-text-link-600 hover:tw-text-link-700"
                >
                  View Document
                </a>
              ) : (
                isTaxExempt &&
                order?.internalAttributes &&
                order?.internalAttributes['taxId'] && (
                  <div className="tw-inline-block tw-text-xs tw-text-gray-600">
                    Tax ID:{order.internalAttributes['taxId']}
                  </div>
                )
              )}
            </div>
          </LabeledText>
          <LabeledText className="tw-flex-1" label="Payment Type">
            <span>
              {paymentType}{' '}
              {invoiceCreditProvider && (
                <span className="tw-text-xs tw-text-gray-600">
                  ({invoiceCreditProvider})
                </span>
              )}
            </span>
            {fulfillment.attributes['revMedCreditAmount'] && (
              <span className="tw-block tw-text-xs tw-text-gray-600">
                Applied RevMed Credit:{' '}
                <Currency
                  currency="USD"
                  value={fulfillment.attributes['revMedCreditAmount']}
                />
              </span>
            )}
          </LabeledText>
        </div>
        <div className="tw-flex-1">
          {fulfillmentType === 'SHIP' &&
            fulfillmentOption &&
            fulfillmentOption.description && (
              <LabeledText className="tw-flex-1" label="Delivery Method">
                {fulfillmentOption.description}
                {shippingOptionMessages.length > 0 && (
                  <>
                    <br />
                    <span className="tw-text-xs tw-text-gray-600">
                      {shippingOptionMessages.join(', ')}
                    </span>
                  </>
                )}
              </LabeledText>
            )}

          {fulfillmentType === 'PICKUP' && (
            <LabeledText className="tw-flex-1" label="Fulfillment Method">
              Pickup
            </LabeledText>
          )}

          {shipments.length > 0 && (
            <LabeledText className="tw-flex-1" label="Tracking Number">
              <div>
                {shipments.map(shipment => (
                  <div key={shipment.trackingNumber}>
                    <a
                      className="tw-text-blue-700"
                      href={shipment.trackingUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {shipment.trackingNumber}
                    </a>
                  </div>
                ))}
              </div>
            </LabeledText>
          )}
          {fulfillment.attributes['poNumber'] && (
            <LabeledText className="tw-flex-1" label="PO Number">
              {fulfillment.attributes['poNumber']}
            </LabeledText>
          )}
          {fulfillment.attributes['paymentNotes'] && (
            <LabeledText className="tw-flex-1" label="Notes">
              {fulfillment.attributes['paymentNotes']}
            </LabeledText>
          )}
          {estimatedDeliveryDate && (
            <LabeledText className="tw-flex-1" label="Est. Delivery Date">
              {formatDate(estimatedDeliveryDate, {
                weekday: 'long',
                month: 'numeric',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
                timeZone: 'UTC'
              })}
            </LabeledText>
          )}
          {fulfillment.attributes['deliveredDateTime'] ? (
            <LabeledText className="tw-flex-1" label="Delivered Date">
              {formatDate(fulfillment.attributes['deliveredDateTime'], {
                weekday: 'long',
                month: 'numeric',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
                timeZone: 'UTC'
              })}
            </LabeledText>
          ) : (
            fulfillment.attributes['pickedUpDateTime'] && (
              <LabeledText className="tw-flex-1" label="Picked Up Date">
                {formatDate(fulfillment.attributes['pickedUpDateTime'], {
                  weekday: 'long',
                  month: 'numeric',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: 'numeric',
                  timeZone: 'UTC'
                })}
              </LabeledText>
            )
          )}
          {fulfillment.attributes['autoAcceptDate'] ? (
            <LabeledText className="tw-flex-1" label="Auto Accept Date">
              {fulfillment.attributes['autoAcceptDate']}
            </LabeledText>
          ) : (
            fulfillment.attributes['inspectionPeriodDays'] && (
              <LabeledText className="tw-flex-1" label="Inspection Period">
                {fulfillment.attributes['inspectionPeriodDays']} Days
              </LabeledText>
            )
          )}
        </div>
      </div>

      <div className="tw-flex tw-flex-col md:tw-flex-row"></div>
    </div>
  );
};

export interface DetailsProps extends IFulfillmentViewCommon {
  className?: string;
}

function getFulfillmentCommission(
  commissionSummary: IOrderFulfillmentCommissions,
  email: string
) {
  return commissionSummary?.commissionLineItems
    .filter(cli => cli.email === email)
    .map(cli => cli.amount)
    .reduce((accumulator, currentValue) => accumulator + currentValue, 0);
}

// purgecss: tw-bg-red-200 tw-text-red-500 tw-text-red-600 tw-bg-blue-200 tw-text-blue-500 tw-text-blue-600 tw-bg-green-200 tw-text-green-500 tw-text-green-600 tw-bg-success-200 tw-text-success-500 tw-text-success-600 tw-bg-orange-200 tw-text-orange-500 tw-text-orange-600 tw-text-yellow-600 tw-bg-yellow-200
const FulfillmentStatusIndicator = ({ metadata, state }) => {
  const { status, attributes, currency } = state.data;
  const userProfile = useUserProfile();
  const formatNumber = useFormatNumber();
  const commissionAmount = getFulfillmentCommission(
    attributes?.commissionSummary,
    userProfile?.email
  );
  const displayStatus = state.data.attributes?.displayStatus || status;

  const fulfillmentStatuses = getFulfillmentStatuses(metadata);
  const currentStatus = find(fulfillmentStatuses, {
    value: displayStatus
  }) || { label: displayStatus, color: 'gray' };

  let { label } = currentStatus;
  const { color } = currentStatus;

  if (status != displayStatus) {
    const { label: currentStatusLabel } = find(fulfillmentStatuses, {
      value: status
    }) || { label: status, color: 'gray' };

    label = currentStatusLabel + ' - ' + label;
  }
  const completionPercentage = getCompletionPercentage(status);

  return (
    <div className="tw-relative tw-w-full tw-pt-1">
      <div className="tw-mb-2 tw-flex tw-items-center tw-justify-between">
        <div>
          <span
            className={`tw-inline-block tw-rounded-full tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-uppercase tw-text-${color}-600 tw-bg-${color}-200`}
          >
            {label}{' '}
            {attributes['statusReason'] && (
              <span className="tw-text-xxs">
                ({attributes['statusReason']})
              </span>
            )}
          </span>
        </div>
        {commissionAmount > 0 && (
          <div>
            <span className="tw-text-sm">Your Commission: </span>
            <span className={`tw-text-xl tw-text-success-500`}>
              {formatNumber(commissionAmount, {
                style: 'currency',
                currency: currency
              })}
            </span>
          </div>
        )}
      </div>
      <div
        className={`tw-mb-4 tw-flex tw-h-2 tw-overflow-hidden tw-rounded tw-text-xs tw-bg-${color}-200`}
      >
        <div
          style={{ width: completionPercentage }}
          className={`tw-flex tw-flex-col tw-justify-center tw-whitespace-nowrap tw-text-center tw-text-white tw-shadow-none tw-bg-${color}-500`}
        />
      </div>
    </div>
  );
};

function getPendingApprovalOrderFulfillmentStatusName(status) {
  switch (status) {
    case 'CANCELLED':
      return 'Cancellation';
    default:
      return '';
  }
}

function getCompletionPercentage(status) {
  switch (status) {
    case 'NEW':
    case 'PENDING_APPROVAL':
      return '0%';
    case 'READY_TO_FULFILL':
      return '12%';
    case 'CAPTURING_PAYMENT':
      return '32%';
    case 'PAYMENT_CAPTURED':
    case 'PAYMENT_CAPTURE_FAILED':
      return '37%';
    case 'FULFILLING':
      return '50%';
    case 'FULFILL_FAILED':
    case 'FULFILLED':
      return '62%';
    case 'DELIVERED':
    case 'DELIVERY_FAILED':
      return '75%';
    case 'BUYER_ACCEPTED':
    case 'BUYER_REJECTED':
    case 'SELLER_PAYOUT_FAILED':
      return '87%';
    case 'COMPLETED':
    case 'CANCELLED':
    case 'DISPUTED':
    case 'RETURNED':
    case 'REFUNDED':
      return '100%';
    default:
      return '0%';
  }
}

const FulfillmentAddress = ({ address }) => {
  return (
    <ul className="tw-list-none tw-text-sm">
      {address.fullName && address.fullName != address.companyName && (
        <li>{address.fullName}</li>
      )}
      <li>{address.companyName}</li>
      <li>
        {`${address.addressLine1} ${address.addressLine2 || ''} ${
          address.addressLine3 || ''
        }`.trim()}
      </li>
      <li>
        {address.city}, {address.stateProvinceRegion} {address.postalCode}
      </li>
      <li>{address.country}</li>
      {address.phonePrimary && (
        <li>Phone: {address.phonePrimary.phoneNumber}</li>
      )}
    </ul>
  );
};

const LabeledText = ({ children, className, label }) => {
  return (
    <div
      className={cx(
        className,
        'sm:tw-text-normal tw-mb-4 tw-flex tw-flex-col tw-text-sm'
      )}
    >
      <span className="tw-text-xs tw-font-medium tw-uppercase tw-tracking-tight tw-text-gray-600">
        {label}
      </span>
      {children}
    </div>
  );
};

function getPaymentTypes(metadata) {
  return getAttribute(metadata, 'paymentTypes', []);
}
